import React, { Component } from 'react';
import { MessageSourceProps, withMessages } from "react-message-source";
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import axios, { AxiosError, CancelTokenSource } from 'axios';
import { deleteQuestionSet, findQuestionSetById, getAllQuestionSets } from 'services/questionSetsServices';
import LoaderComponent from 'components/LoaderComponent';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import PxGrid from 'components/layout/PxGrid';
import { Grid, Modal } from 'semantic-ui-react';
import AddButton from 'components/AddButton';
import { QuestionSetDto } from 'ts-types/api.types';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import UpsertQuestionSetForm from 'components/settings/question-sets/UpsertQuestionSetForm';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import EditActionButton from 'components/EditActionButton';
import DeleteActionButton from 'components/DeleteActionButton';
import { noop } from 'utils/functionUtils';
import { StyledInput } from 'components/final-form/Input';
import { CheckboxSc } from 'components/final-form/CheckBox';
import SearchResultComponent from 'components/SearchResultComponent';

interface Props extends MessageSourceProps {

}

interface State {
  hasError: boolean;
  errorMessage?: string;
  questionSetsLoaded: boolean;
  questionSet?: QuestionSetDto,
  questionSetId?: number,
  addEditModalOpen: boolean,
  questionSets: Array<QuestionSetDto>;
  questionSetsFiltered: Array<QuestionSetDto>;
  searchTerm: string;
  includingInactive: boolean;
  cancelTokenSource: CancelTokenSource;
}

class QuestionSetsConfig extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      hasError: false,
      questionSetsLoaded: false,
      questionSets: [],
      questionSetsFiltered: [],
      addEditModalOpen: false,
      searchTerm: "",
      includingInactive: false,
      cancelTokenSource
    }
  }

  componentDidMount() {
    this.mounted = true;

    getAllQuestionSets(this.state.cancelTokenSource)
        .then(questionSets => {
          const questionSetsFiltered = questionSets.filter(q => q.active)
          this.setState({questionSets, questionSetsFiltered})
        })
        .catch(error => {
          this.setState({hasError: true});
          this.handleError(error);
        })
        .finally(() => this.setState({questionSetsLoaded: true}));
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource && cancelTokenSource.cancel('Operation canceled by user');
  }

  handleError = (error: AxiosError) => {
    const {getMessage} = this.props;
    this.setState({errorMessage: getMessage(error.message)});
  }

  handleSaveUpsertForm = (questionSetDto: QuestionSetDto) => {

    const {includingInactive} = this.state;

    this.setState(oldState => {
      const questionSets = oldState.questionSets;
      const indexOldElement = questionSets.findIndex(({id}) => id == questionSetDto.id);

      let newQuestionSets;
      if (indexOldElement !== -1) {
        newQuestionSets = [...questionSets.slice(0, indexOldElement), questionSetDto, ...questionSets.slice(indexOldElement + 1)]
      } else {
        newQuestionSets = [...questionSets, questionSetDto];
      }

      let questionSetsFiltered = newQuestionSets;
      if(!includingInactive){
         questionSetsFiltered = newQuestionSets.filter(q => q.active);
      }

      return {
        questionSets: newQuestionSets,
        questionSetsFiltered: questionSetsFiltered
      }
    });
    this.hideModal();
  }

  openAddEditModal = (id: number | undefined) => {

    const {cancelTokenSource} = this.state;

    if (id) {
      findQuestionSetById(id, cancelTokenSource).then(response => {
        this.showModal(id, response);
      }).catch(noop);
    }
  };

  showModal = (id?: number, questionSet?: QuestionSetDto) => {
    this.setState(({}: State) => {
      return {
        addEditModalOpen: true,
        questionSet: questionSet,
        questionSetId: id
      }
    });
  };

  hideModal = () => {
    this.setState(({}: State) => ({
      addEditModalOpen: false,
      questionSet: undefined,
      questionSetId: undefined
    }));
  };

  handleSearchChange = async (searchTerm: string): Promise<any> => {
    if (!this.mounted) {
      return;
    }

    this.setState({
      questionSetsLoaded: false
    })

    const {includingInactive} = this.state;
    await setTimeout(() => {
      this.setState((oldState) => {
        let questionSetsFiltered = oldState.questionSets
            .filter(questionSet => questionSet.name.toLowerCase().includes(searchTerm.toLowerCase()));
        if (!includingInactive) {
          questionSetsFiltered = questionSetsFiltered.filter(q => q.active);
        }
        return {questionSetsFiltered, searchTerm}
      });
      this.setState({
        questionSetsLoaded: true
      })
    }, 300);
  }

  handleCheckBoxChange = (): void => {

    const {searchTerm} = this.state;

    this.setState((oldState) => {

      const newIncludingInactiveState = !oldState.includingInactive;

      let questionSetsFiltered = oldState.questionSets
          .filter(questionSet => questionSet.name.toLowerCase().includes(searchTerm.toLowerCase()));

      if (!newIncludingInactiveState) {
        questionSetsFiltered = questionSetsFiltered.filter(q => q.active);
      }

      return {
        questionSetsFiltered,
        includingInactive: newIncludingInactiveState
      }
    });

  };

  deleteQuestionSet = (id: number): void => {
    const {cancelTokenSource} = this.state;
    deleteQuestionSet(id, cancelTokenSource).then(response => {
      this.setState(({questionSets}: State) => {
        const newQuestionSets = questionSets.filter((questionSet: QuestionSetDto) => questionSet.id != response.id);
        return ({
          questionSets: newQuestionSets,
          questionSetsFiltered: newQuestionSets
        });
      })
    }).catch(noop);
  };

  render() {

    const {getMessage} = this.props;
    const {questionSetsLoaded, hasError} = this.state;

    return (
        <React.Fragment>
          {hasError && <StyledErrorMessage>{this.state.errorMessage}</StyledErrorMessage>}
          <PraxStyledSegment>
            <div>
              {this.renderSearchQuestionSetsSegment()}
            </div>

            <StyledViewTableRow>
              {
                questionSetsLoaded
                    ? this.renderQuestionSets()
                    : <LoaderComponent message={getMessage("questionSet.loading")}/>
              }
            </StyledViewTableRow>
          </PraxStyledSegment>

          {this.renderQuestionSetsModal()}
        </React.Fragment>
    )
  }

  renderSearchQuestionSetsSegment = (): React.ReactNode => {
    const {getMessage} = this.props;
    const {includingInactive, questionSetsFiltered} = this.state;

    const numResults = questionSetsFiltered.length;

    return (
        <PxGrid>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
              {getMessage("select")}:
            </Grid.Column>
            <Grid.Column width={13}>
              <StyledInput
                  name="description"
                  fluid
                  autoFocus
                  autoComplete="off"
                  onChange={(event: any) => this.handleSearchChange(event.target.value)}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={3}>
            </Grid.Column>
            <Grid.Column width={4}>
              <CheckboxSc
                  checked={includingInactive}
                  label={getMessage("includingInactive")}
                  toggle={true}
                  onChange={() => this.handleCheckBoxChange()}
              />
            </Grid.Column>
            <Grid.Column width={9}>
              <AddButton buttonMessage={getMessage("button.add")} onClick={() => this.showModal()}/>
            </Grid.Column>
          </Grid.Row>
          <SearchResultComponent numResults={numResults} textMessage={"search.resultsSummary"}/>
        </PxGrid>
    );
  };

  statusCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;

    if (cellData) {
      return getMessage("questionSet.active")
    }
    return getMessage("questionSet.inactive");
  };

  renderQuestionSets = (): React.ReactNode => {
    const {getMessage} = this.props;
    const {questionSetsFiltered} = this.state;

    return (
        <VirtualizedTable
            rowCount={questionSetsFiltered.length}
            rowGetter={this.questionSetRowGetter}
            rowRenderer={this.foundQuestionSetRowRenderer}
            columns={[
              {
                width: 100,
                label: "#",
                dataKey: "index"
              },
              {
                width: 300,
                flexGrow: 1,
                label: (getMessage("questionSet.name")),
                dataKey: "name"
              },
              {
                width: 150,
                label: (getMessage("status")),
                dataKey: "active",
                cellRenderer: this.statusCellRenderer
              },
              {
                width: 100,
                label: (getMessage("actions")),
                dataKey: "id",
                cellRenderer: (this.actionsCellRenderer)
              }
            ]}
        />
    );
  }

  actionsCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <EditActionButton
              hoverMessage={getMessage('msgEdit')}
              onConfirm={() => this.openAddEditModal(cellData)}
          />
          <DeleteActionButton
              hoverMessage={getMessage('msgDelete')}
              popupMessage={getMessage("questionSet.confirmDelete")}
              onConfirm={() => this.deleteQuestionSet(cellData)}
          />
        </React.Fragment>
    );
  };

  questionSetRowGetter = ({index}: any) => {
    const {questionSetsFiltered} = this.state;

    Object.assign(questionSetsFiltered[index], {index: index + 1});

    return questionSetsFiltered[index];
  };

  foundQuestionSetRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};
    const {questionSetsFiltered} = this.state;
    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
            onDoubleClick={() => this.openAddEditModal(questionSetsFiltered[index].id)}
        >
          {columns}
        </div>
    );
  };

  renderQuestionSetsModal = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {addEditModalOpen, questionSet, questionSetId} = this.state;

    return (
        <Modal
            open={addEditModalOpen}
            onClose={() => this.hideModal()}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("questionSet.form.header")}</Modal.Header>
          <FormModalContentContainer>
            <UpsertQuestionSetForm
                questionSet={questionSet}
                questionSetId={questionSetId}
                onSave={this.handleSaveUpsertForm}
                onCancel={this.hideModal}
            />
          </FormModalContentContainer>
        </Modal>
    );
  };
}

export default withMessages(QuestionSetsConfig);
